import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Table, Tabs, Tag, Typography } from 'antd';
import styles from './PlanPresentation.less';
import { getMemberCurrentPlanSchedule } from '@/services/technical';
import { readPlanSchedule } from './currentPlan';
import CurrentPlanCourse from './CurrentPlanCourse';

export default function CurrentPlanSchedule({ memberId, currentPlanId }) {
  const [state, setState] = useState({});
  const [selected, setSelected] = useState(null);
  const generation = useRef(0);
  const identity = `${memberId}:${currentPlanId}`;
  useEffect(() => {
    generation.current += 1;
    setState({});
    setSelected(null);
    return () => {
      generation.current += 1;
    };
  }, [identity]);
  const current = state.identity === identity ? state : {};
  const load = async () => {
    const request = ++generation.current;
    setState({ identity, loading: true });
    try {
      const query = { memberId, currentPlanId };
      const response = await getMemberCurrentPlanSchedule({
        requestData: query,
      });
      const data = readPlanSchedule(response, query);
      if (generation.current === request) setState({ identity, data });
    } catch (error) {
      if (generation.current !== request) return;
      const status = error?.response?.status;
      const messages = {
        401: '请先登录后台',
        403: '无会员档案菜单权限',
        409: '计划已变化，请刷新上方计划概览',
        422: '当前计划课程规模暂不支持完整排期展示',
      };
      setState({
        identity,
        error: messages[status] || '课程安排暂时无法加载，请重试',
      });
    }
  };
  if (currentPlanId == null)
    return (
      <Typography.Paragraph type="secondary">
        计划来源尚未提供实例标识，暂不能加载课程安排。
      </Typography.Paragraph>
    );
  const courses = current.data
    ? current.data.groups.reduce((all, group) => all.concat(group.courses), [])
    : [];
  const completedCount = courses.filter(
    (course) => course.completed === true,
  ).length;
  const activeGroup = current.data?.groups.find(
    (group) => group.status === '进行中',
  );
  return (
    <div className={styles.schedule}>
      {current.error && (
        <Typography.Paragraph type="warning">
          {current.error}
        </Typography.Paragraph>
      )}
      {current.data && (
        <>
          <div className={styles.scheduleOverview}>
            <div>
              <span>计划课程</span>
              <strong>{courses.length} 节</strong>
            </div>
            <div>
              <span>已完成</span>
              <strong>{completedCount} 节</strong>
            </div>
            <div>
              <span>当前阶段</span>
              <strong>{activeGroup?.title || '未标记'}</strong>
            </div>
          </div>
          {!current.data.groups.some((group) => group.courses.length > 0) && (
            <Typography.Paragraph>当前计划暂无课程安排</Typography.Paragraph>
          )}
          <div className={styles.scheduleNotes}>
            {current.data.summary && (
              <details className={styles.scheduleDescription}>
                <summary>计划说明</summary>
                <Typography.Paragraph>
                  {current.data.summary}
                </Typography.Paragraph>
              </details>
            )}
            <details className={styles.scheduleDescription}>
              <summary>数据口径</summary>
              <Typography.Paragraph type="secondary">
                排期与小程序一致；未提供日期不代表已预约。预计时长、动作数量为计划课程口径，不代表实际完成量。
              </Typography.Paragraph>
            </details>
          </div>
          <Tabs
            className={styles.stageTabs}
            defaultActiveKey={String(
              Math.max(
                0,
                current.data.groups.findIndex(
                  (group) => group.status === '进行中',
                ),
              ),
            )}
          >
            {current.data.groups.map((group, index) => (
              <Tabs.TabPane
                key={String(index)}
                tab={
                  <span className={styles.stageTabLabel}>
                    {group.title || `阶段 ${index + 1}`}
                    {group.status && <small>{group.status}</small>}
                  </span>
                }
              >
                <div className={styles.stageHeading}>
                  <div className={styles.stageTitleRow}>
                    <div>
                      <Typography.Paragraph strong>
                        {group.title || '课程安排'}
                      </Typography.Paragraph>
                      {group.courseRange && (
                        <Typography.Text type="secondary">
                          {group.courseRange}
                        </Typography.Text>
                      )}
                    </div>
                    {group.status && (
                      <Tag
                        color={group.status === '进行中' ? 'blue' : undefined}
                      >
                        {group.status}
                      </Tag>
                    )}
                  </div>
                  {group.description && (
                    <Typography.Paragraph className={styles.stageDescription}>
                      {group.description}
                    </Typography.Paragraph>
                  )}
                </div>
                <Table
                  className={styles.courseTable}
                  size="small"
                  scroll={{ x: 960 }}
                  rowKey="sequence"
                  dataSource={group.courses}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  locale={{ emptyText: '当前计划暂无课程安排' }}
                  columns={[
                    {
                      title: '课程',
                      dataIndex: 'name',
                      render: (value, row) => (
                        <div className={styles.courseName}>
                          <span>{row.sequence}</span>
                          <strong>{value}</strong>
                        </div>
                      ),
                    },
                    {
                      title: '训练内容',
                      dataIndex: 'details',
                      render: (_, row) => (
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            setSelected({
                              identity,
                              courseType: row.courseType,
                              courseIdent: row.courseIdent,
                            })
                          }
                        >
                          动作与参数
                        </Button>
                      ),
                    },
                    {
                      title: '课程规模',
                      dataIndex: 'scale',
                      render: (_, row) => (
                        <div className={styles.courseScale}>
                          <span>
                            {row.estimateMinutes == null
                              ? '时长未提供'
                              : `${row.estimateMinutes} 分钟`}
                          </span>
                          <span>
                            {row.actionCount == null
                              ? '动作数未提供'
                              : `${row.actionCount} 个动作`}
                          </span>
                        </div>
                      ),
                    },
                    {
                      title: '排期 / 完成日期',
                      dataIndex: 'date',
                      render: (value, row) =>
                        value ? `${row.week || ''} ${value}`.trim() : '未提供',
                    },
                    {
                      title: '器械',
                      dataIndex: 'instruments',
                      render: (value) => value || '未提供',
                    },
                    {
                      title: '完成状态',
                      dataIndex: 'completed',
                      render: (value) => (
                        <Tag color={value === true ? 'green' : undefined}>
                          {value === true
                            ? '已完成'
                            : value === false
                            ? '待训练'
                            : '状态未知'}
                        </Tag>
                      ),
                    },
                  ]}
                />
              </Tabs.TabPane>
            ))}
          </Tabs>
        </>
      )}
      {!current.data && (
        <Button size="small" disabled={!!current.loading} onClick={load}>
          {current.loading
            ? '加载课程中…'
            : current.error
            ? '重试课程安排'
            : '查看课程安排'}
        </Button>
      )}
      {selected?.identity === identity && (
        <CurrentPlanCourse
          key={`${identity}:${selected.courseType}:${selected.courseIdent}`}
          memberId={memberId}
          currentPlanId={currentPlanId}
          courseType={selected.courseType}
          courseIdent={selected.courseIdent}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
CurrentPlanSchedule.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  currentPlanId: PropTypes.number,
};
CurrentPlanSchedule.defaultProps = { currentPlanId: null };
